<template>
  <div>
    <navbar :links="['dashboard', $route.name]" />
    <div class="mt-4 mb-3 headline">
      {{ $t('groupContentStoreStatisticsTitle') }}
    </div>
    <div class="grey--text text--darken-2 subheading">
      {{ $t('groupContentStoreStatisticsHint') }}
    </div>
    <v-layout
      wrap
      class="mt-3"
    >
      <!-- Select Radio or TV -->
      <v-select
        v-model="ctypesSelected"
        :items="ctypesChoices"
        :label="$t('groupContentStoreStatisticsCtypes')"
        :disabled="loading"
        multiple
        hide-details
        class="mr-5"
        style="max-width: 140px"
      />
      <!-- Select optional startdate -->
      <v-layout
        nowrap
        align-baseline
        :class="{'mt-4': $vuetify.breakpoint.xs}"
      >
        <v-menu
          v-model="startDateMenu"
          class="mr-2"
          lazy
          offset-y
          :min-width="mobileDatepickerMinWidth"
          :nudge-left="40"
          :disabled="loading"
        >
          <v-text-field
            slot="activator"
            v-model="startDate"
            prepend-icon="event"
            readonly
            :label="$t('groupContentStoreStatisticsOptionalStartdate')"
            :disabled="loading"
          />
          <v-date-picker
            v-model="startDate"
            color="msr"
            scrollable
            event-color="msr"
            :no-title="$vuetify.breakpoint.xsOnly"
            :first-day-of-week="1"
            :locale="$t('_datepickerLocale')"
            :allowed-dates="isWithinStatisticsTimeframe"
          />
        </v-menu>
        <!-- Clear startDate button -->
        <v-btn
          v-if="startDate"
          flat
          icon
          class="ml-0"
          :disabled="loading"
          @click="startDate = ''"
        >
          <v-icon class="msr--text">
            clear
          </v-icon>
        </v-btn>
      </v-layout>
      <!-- Select optional stopdate -->
      <v-layout
        nowrap
        align-baseline
        :class="{'mt-4': $vuetify.breakpoint.xs}"
      >
        <v-menu
          v-model="stopDateMenu"
          class="mr-2"
          lazy
          offset-y
          :min-width="mobileDatepickerMinWidth"
          :nudge-left="40"
          :disabled="loading"
        >
          <v-text-field
            slot="activator"
            v-model="stopDate"
            prepend-icon="event"
            readonly
            :label="$t('groupContentStoreStatisticsOptionalStopdate')"
            :disabled="loading"
          />
          <v-date-picker
            v-model="stopDate"
            color="msr"
            scrollable
            event-color="msr"
            :no-title="$vuetify.breakpoint.xsOnly"
            :first-day-of-week="1"
            :locale="$t('_datepickerLocale')"
            :allowed-dates="isWithinStatisticsTimeframe"
          />
        </v-menu>
        <!-- Clear stopDate button -->
        <v-btn
          v-if="stopDate"
          flat
          icon
          class="ml-0"
          :disabled="loading"
          @click="stopDate = ''"
        >
          <v-icon class="msr--text">
            clear
          </v-icon>
        </v-btn>
      </v-layout>
    </v-layout>
    <!-- Select Groups -->
    <v-select
      v-model="selectedGroupIds"
      :items="groupsValidForStatistics"
      :label="$t('groups')"
      :disabled="loading"
      multiple
      persistent-hint
      item-text="name"
      item-value="id"
      :class="{'mt-3': $vuetify.breakpoint.smAndUp}"
    />
    <!-- Submit button -->
    <v-layout justify-end>
      <v-btn
        color="msr"
        class="mr-0"
        round
        :block="$vuetify.breakpoint.xs"
        :dark="enableSubmission"
        :disabled="!enableSubmission"
        :loading="loading"
        @click="submitStatisticsRequest()"
      >
        <v-layout
          justify-center
          align-center
        >
          <v-icon
            v-if="$vuetify.breakpoint.smAndUp"
            medium
            left
          >
            bar_chart
          </v-icon>
          {{ $t('groupContentStoreStatisticsSubmit') }}
          <v-icon
            v-if="$vuetify.breakpoint.smAndUp"
            medium
            right
          >
            mail
          </v-icon>
        </v-layout>
      </v-btn>
    </v-layout>
  </div>
</template>

<script>
import orderBy from 'lodash.orderby';

import mobileDatepickerMinWidth from '@/mixins/mobileDatepickerMinWidth.js';
import timing from '@/mixins/timing';

import Navbar from '@/components/Navbar';

const BEGINNING_OF_STATISTICS = '2019-02-01';

export default {
  components: {
    Navbar,
  },
  mixins: [mobileDatepickerMinWidth, timing],
  data() {
    const ctypeRadio = {
      value: 'radio',
      text: this.$t('radio'),
    };
    const ctypeTv = {
      value: 'tv',
      text: this.$t('tv'),
    };

    return {
      ctypesChoices: [ctypeRadio, ctypeTv],
      ctypesSelected: [ctypeTv.value],

      startDateMenu: false,
      stopDateMenu: false,

      selectedGroupIds: [],
      loading: false,
    };
  },
  computed: {
    enableSubmission() {
      return this.ctypesSelected.length > 0 && this.selectedGroupIds.length > 0;
    },
    groupsValidForStatistics() {
      return orderBy(
        this.$userprofile.accessible_groups.filter((group) => {
          return group.kind !== 'INDUSTRY';
        }),
        'name'
      );
    },
  },
  mounted() {
    // Preselect Group if we only have one to choose.
    if (this.groupsValidForStatistics.length === 1) {
      this.selectedGroupIds = [this.groupsValidForStatistics[0].id];
    }
  },
  methods: {
    isWithinStatisticsTimeframe(dateString) {
      if (!dateString) {
        return false;
      }
      return (
        this._isAfterOrSameAs(dateString, BEGINNING_OF_STATISTICS) &&
        this.isNotInFuture(dateString)
      );
    },
    submitStatisticsRequest() {
      this.loading = true;

      const statisticsStartDate = this.startDate || BEGINNING_OF_STATISTICS;
      const statisticsStopDate = this.stopDate;

      this.$api
        .submitGroupsContentsStoreStatisticsRequest(
          this.ctypesSelected,
          this.selectedGroupIds,
          statisticsStartDate,
          statisticsStopDate
        )
        .then((response) => {
          const successMessage = this.$t(
            'groupContentStoreStatisticsSubmitted'
          ).replace('{{ email }}', this.$user.email);
          this.$bus.$emit('alert-success', {
            message: successMessage,
            translate: false,
            timeout: 10000,
          });
        })
        .catch((error) => {
          this.$bus.$emit('alert-error', {
            message: 'genericFailedInfo',
            translate: true,
            timeout: 10000,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/Statistics.vue